import {
  collection,
  doc,
  getDocs,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../services/firebase";

/**
 * Elimina una conversación específica y todos sus mensajes
 * @param {string} conversationId - ID de la conversación a eliminar
 * @param {string} userId - ID del usuario actual
 * @returns {Promise<boolean>} - True si se eliminó correctamente
 */
export const deleteConversation = async (conversationId, userId) => {
  if (!conversationId || !userId) return false;

  try {
    // 1. Eliminar mensajes de la conversación
    const messagesRef = collection(db, "chat_messages");
    const q = query(messagesRef, where("conversationId", "==", conversationId));
    const snapshot = await getDocs(q);

    const batch = writeBatch(db);
    snapshot.docs.forEach((docSnap) => {
      batch.delete(doc(db, "chat_messages", docSnap.id));
    });

    // 2. Eliminar la conversación
    batch.delete(doc(db, "chat_conversations", conversationId));
    await batch.commit();

    return true;
  } catch (error) {
    console.error("Error al eliminar conversación:", error);
    return false;
  }
};

/**
 * Elimina todas las conversaciones de un usuario
 * @param {string} userId - ID del usuario actual
 * @returns {Promise<boolean>} - True si se eliminó correctamente
 */
export const deleteAllConversations = async (userId) => {
  if (!userId) return false;

  try {
    // 1. Obtener todas las conversaciones del usuario
    const conversationsRef = collection(db, "chat_conversations");
    const q = query(conversationsRef, where("userId", "==", userId));
    const snapshot = await getDocs(q);

    // Verificar si hay conversaciones
    if (snapshot.empty) return true;

    // 2. Para cada conversación, obtener y eliminar sus mensajes
    const conversationIds = snapshot.docs.map((doc) => doc.id);

    // Hacer lotes más pequeños para respetar límites de Firestore
    const batchSize = 400; // Firestore tiene un límite de 500 operaciones por lote
    // 3. Eliminar mensajes por lotes
    for (const convId of conversationIds) {
      const msgQuery = query(
        collection(db, "chat_messages"),
        where("conversationId", "==", convId),
      );
      const msgSnapshot = await getDocs(msgQuery);

      // Crear nuevo batch si es necesario
      const msgBatch = writeBatch(db);
      let operationCount = 0;

      for (const docSnap of msgSnapshot.docs) {
        msgBatch.delete(doc(db, "chat_messages", docSnap.id));
        operationCount++;

        // Si alcanzamos el límite, ejecutar este lote y crear uno nuevo
        if (operationCount >= batchSize) {
          await msgBatch.commit();
          operationCount = 0;
        }
      }

      // Ejecutar el lote final si hay operaciones pendientes
      if (operationCount > 0) {
        await msgBatch.commit();
      }
    }

    // 4. Eliminar las conversaciones
    const convBatch = writeBatch(db);
    snapshot.docs.forEach((docSnap) => {
      convBatch.delete(doc(db, "chat_conversations", docSnap.id));
    });
    await convBatch.commit();

    return true;
  } catch (error) {
    console.error("Error eliminando historial:", error);
    return false;
  }
};
